/**
 * Game sounds hook using ElevenLabs AI sound generation
 * Provides sound effects for games with caching
 */

import { useState, useCallback, useRef } from 'react';

export type GameSoundType = 
  | 'correct' 
  | 'incorrect' 
  | 'levelComplete' 
  | 'achievement' 
  | 'celebration' 
  | 'streak'
  | 'cardFlip'
  | 'timer';

interface SoundPrompts {
  [key: string]: string;
}

// Sound prompts - soothing singing bowl theme for wellbeing app
const SOUND_PROMPTS: SoundPrompts = {
  correct: 'Single Tibetan singing bowl strike, gentle resonant tone, peaceful meditation bell, warm and calming',
  incorrect: 'Very soft low singing bowl hum, gentle quiet resonance, subtle muted tone',
  levelComplete: 'Multiple Tibetan singing bowls resonating together, peaceful harmonic cascade, meditative crescendo, serene celebration',
  achievement: 'Deep resonant singing bowl with gentle overtones, peaceful golden tone, warm meditative chime',
  celebration: 'Layered singing bowls creating peaceful harmony, gentle resonant waves, calming celebration tones',
  streak: 'Two singing bowls in harmony, gentle ascending tones, peaceful resonance',
  cardFlip: 'Very soft singing bowl tap, quiet gentle ring, subtle meditation bell',
  timer: 'Soft distant singing bowl, quiet peaceful tone, gentle ambient hum',
};

// Duration in seconds for each sound type
const SOUND_DURATIONS: { [key: string]: number } = {
  correct: 1,
  incorrect: 0.8,
  levelComplete: 2.5,
  achievement: 2,
  celebration: 3,
  streak: 1.5,
  cardFlip: 0.5,
  timer: 1,
};

// Cache for generated sounds
const soundCache = new Map<string, string>();

export const useGameSounds = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [soundsEnabled, setSoundsEnabled] = useState(true);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  const generateSound = useCallback(async (type: GameSoundType): Promise<string | null> => {
    // Check cache first
    if (soundCache.has(type)) {
      return soundCache.get(type)!;
    }

    const prompt = SOUND_PROMPTS[type];
    const duration = SOUND_DURATIONS[type];

    if (!prompt) {
      console.warn(`Unknown sound type: ${type}`);
      return null;
    }

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-sfx`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ prompt, duration }),
        }
      );

      if (!response.ok) {
        throw new Error(`Sound generation failed: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Cache the URL
      soundCache.set(type, audioUrl);
      
      return audioUrl;
    } catch (error) {
      console.error('Failed to generate sound:', error);
      return null;
    }
  }, []);

  const playSound = useCallback(async (type: GameSoundType) => {
    if (!soundsEnabled) return;

    setIsLoading(true);

    try {
      const audioUrl = await generateSound(type);
      
      if (audioUrl) {
        // Stop any currently playing sound
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current.currentTime = 0;
        }

        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio;
        await audio.play();
      }
    } catch (error) {
      console.error('Failed to play sound:', error);
    } finally {
      setIsLoading(false);
    }
  }, [soundsEnabled, generateSound]);

  // Pre-load common sounds for faster playback
  const preloadSounds = useCallback(async (types: GameSoundType[]) => {
    setIsLoading(true);
    
    try {
      await Promise.all(types.map(type => generateSound(type)));
    } catch (error) {
      console.error('Failed to preload sounds:', error);
    } finally {
      setIsLoading(false);
    }
  }, [generateSound]);

  const stopSound = useCallback(() => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
    }
  }, []);

  const toggleSounds = useCallback(() => {
    setSoundsEnabled(prev => !prev);
  }, []);

  return {
    playSound,
    preloadSounds,
    stopSound,
    toggleSounds,
    isLoading,
    soundsEnabled,
  };
};

// Singleton instance for global sound control
let globalSoundsEnabled = true;

export const setGlobalSoundsEnabled = (enabled: boolean) => {
  globalSoundsEnabled = enabled;
};

export const getGlobalSoundsEnabled = () => globalSoundsEnabled;
